import { useEffect, useRef } from "react";
import { Home, BookOpen, Mic, Flame, User } from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useCollections } from "@/hooks/useCollections";
import { useEvent } from "@/lib/events";
import type { RecordingOpts } from "@/components/HomeFeed";
import type { SpaceMemberRole } from "@/lib/supabase";

interface BottomNavProps {
  onRecord: (opts?: RecordingOpts & { autoStart?: boolean }) => void;
  activeSpaceRole?: SpaceMemberRole | null;
}

type Tab =
  | { to: string; icon: typeof Home; label: string; isRecord?: false }
  | { to: string; icon: typeof Mic; label: string; isRecord: true };

const BottomNav = ({ onRecord, activeSpaceRole }: BottomNavProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { activeCollection, refresh } = useCollections();

  // Active-capture state can be mutated from other components (CollectionDetailSheet).
  // Each useCollections() call has its own local state, so listen for the bus events
  // and refresh this instance when they fire.
  useEvent("collection.active_mode.started", () => refresh(), [refresh]);
  useEvent("collection.active_mode.ended", () => refresh(), [refresh]);

  const longPressTimerRef = useRef<number | null>(null);
  const longPressFiredRef = useRef(false);
  const clearLongPressTimer = () => {
    if (longPressTimerRef.current !== null) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };
  const startLongPress = () => {
    longPressFiredRef.current = false;
    clearLongPressTimer();
    longPressTimerRef.current = window.setTimeout(() => {
      longPressFiredRef.current = true;
      onRecord();
    }, 500);
  };
  const cancelLongPress = () => {
    clearLongPressTimer();
  };
  const handleFabClick = () => {
    if (longPressFiredRef.current) {
      longPressFiredRef.current = false;
      return;
    }
    onRecord();
  };
  useEffect(() => () => clearLongPressTimer(), []);

  const allTabs: Tab[] = [
    { to: "/", icon: Home, label: t("nav.home") },
    { to: "/feed", icon: BookOpen, label: t("nav.stories") },
    { to: "#record", icon: Mic, label: t("nav.record"), isRecord: true },
    { to: "/collections", icon: Flame, label: t("nav.collections") },
    { to: "/profile", icon: User, label: t("nav.profile") },
  ];

  const CHILD_TABS = new Set(["/", "/feed", "/profile"]);
  // Contributors keep the FAB (recording is their whole job) and Tribe (so they
  // can see which family they're in) but lose Collections — they can't create
  // or edit collections per RLS anyway.
  const CONTRIBUTOR_TABS = new Set(["/", "/feed", "#record", "/tribe", "/profile"]);
  const tabs =
    activeSpaceRole === "child"
      ? allTabs.filter((tab) => !tab.isRecord && CHILD_TABS.has(tab.to))
      : activeSpaceRole === "contributor"
      ? allTabs.filter((tab) => CONTRIBUTOR_TABS.has(tab.to))
      : allTabs;

  const pillColor = activeCollection?.color;

  return (
    <>
      <nav
        className="shrink-0 relative z-50 bg-card/95 backdrop-blur-md border-t border-border/50 px-2"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <div className="flex items-end justify-around h-16">
          {tabs.map((tab) => {
            if (tab.isRecord) {
              const fabBg = pillColor ?? "hsl(var(--primary))";
              const fabShadow = pillColor
                ? `0 4px 16px -2px ${pillColor}55`
                : "0 4px 16px -2px hsl(124 14% 54% / 0.30)";
              return (
                <div key="record" className="relative -mt-4 flex flex-col items-center">
                  <button
                    type="button"
                    onClick={handleFabClick}
                    onPointerDown={startLongPress}
                    onPointerUp={cancelLongPress}
                    onPointerCancel={cancelLongPress}
                    onPointerLeave={cancelLongPress}
                    onContextMenu={(e) => e.preventDefault()}
                    aria-label={t("nav.record")}
                    className="flex items-center justify-center w-14 h-14 rounded-full shadow-lg transition-transform hover:scale-[1.03] active:scale-95 select-none"
                    style={{
                      backgroundColor: fabBg,
                      color: "hsl(var(--primary-foreground))",
                      boxShadow: fabShadow,
                      WebkitTouchCallout: "none",
                      touchAction: "manipulation",
                    }}
                  >
                    <span className="text-2xl leading-none" aria-hidden>
                      {activeCollection?.icon ?? "🐿️"}
                    </span>
                  </button>
                </div>
              );
            }

            const isActive = location.pathname === tab.to;

            return (
              <NavLink
                key={tab.to}
                to={tab.to}
                className="flex flex-col items-center justify-center gap-0.5 py-2 px-3 min-w-[56px]"
              >
                <tab.icon
                  className={cn(
                    "h-5 w-5 transition-colors",
                    isActive ? "text-primary" : "text-muted-foreground"
                  )}
                />
                <span
                  className={cn(
                    "text-[10px] font-medium transition-colors",
                    isActive ? "text-primary" : "text-muted-foreground"
                  )}
                >
                  {tab.label}
                </span>
              </NavLink>
            );
          })}
        </div>
      </nav>
    </>
  );
};

export default BottomNav;
